import { moneyDay, pounds, settleMoney, type MoneyAccount, type MoneyQuestion, type Settled, type SettledAccount, type SqueegeeMoney } from './squeegee-money'

// The import's money from a Squeegee backup (PLAN section 4, release step 6; Liam's answers 2 Oct 2026), on the phone.
// Pure: what to send with each customer, what the owner is asked, and the words of the "Money owed and credit" card.
// The card itself is drawn by src/screens/import.ts. Only on a round with brought money switched on.

// An account already on the round (GET /api/brought-accounts), so a second import adds nothing twice and a newer
// backup offers its difference (Liam Q10).
// state and batch_key: an account whose history is still arriving is finished under its own import (Astra, steps 5-6
// finding 4).
export interface KnownAccount { id: number; customer_id: number; squeegee_ref: string; squeegee_id: string | null; accepted_pence: number; as_of: string; state?: string; batch_key?: string | null }

export interface MoneyState {
  m: SqueegeeMoney
  answers: Record<string, string>
  // Why money cannot come from this backup (Liam Q11: a backup from before today), or null.
  refused: string | null
  known: KnownAccount[]
}

// What each account sends to the server (functions/lib/brought-import.ts Brought).
export interface Brought {
  squeegee_ref: string
  squeegee_id: string
  as_of: string
  squeegee_pence: number
  opening_pence: number
  collections: { ext_id: string; provider: string; provider_payment_id: string | null; amount_pence: number; on_date: string | null; squeegee_status: string; opening_effect_pence: number }[]
  history_expected: number
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// "Fri 2 Oct, 7:02pm" in the round's time zone.
export function asOfWords(asOf: string, timeZone: string): string {
  const at = new Date(asOf)
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at)
  const p = (t: string): number => Number(parts.find((x) => x.type === t)?.value ?? 0)
  const day = new Date(Date.UTC(p('year'), p('month') - 1, p('day'))).getUTCDay()
  const h = p('hour')
  return `${DAY[day]} ${p('day')} ${MON[p('month') - 1]}, ${h % 12 === 0 ? 12 : h % 12}:${String(p('minute')).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`
}

// Liam Q11: on switch day, stop taking money in Squeegee once the backup is made, and import that backup. Money from a
// backup made before today (the round's time zone), or one whose time can't be read, is refused; the customers can
// still come in.
export function moneyRefusal(asOf: string | null, today: string, timeZone: string): string | null {
  if (!asOf) return "Roundly can't tell when this backup was made, so its money can't come in. In Squeegee's Manage Backups, make a new backup today and choose that one."
  const day = moneyDay(asOf, timeZone)
  if (day < today) return `This backup is from ${day.slice(8, 10).replace(/^0/, '')} ${MON[Number(day.slice(5, 7)) - 1]}, before today, so its money can't come in: Squeegee may have taken payments since. Stop taking money in Squeegee, make a new backup today and choose that one. Your customers can still come in now.`
  return null
}

// Whether a backup has money to check (Astra, steps 5-6 finding 6): any money record, history line, direct debit or
// question, an account already here, or money not brought. A backup whose every customer is settled at £0 still has
// accounts to bring (zero ones), their history, and the last visits in their opening balances.
export function moneyToCheck(m: SqueegeeMoney): boolean {
  return m.questions.length > 0 || m.not_brought.length > 0
    || m.accounts.some((a) => a.squeegee_pence !== 0 || a.collections.length > 0 || a.already !== null || Object.keys(a.records).length > 0 || a.lines.length > 0)
}

// A backup whose customers are all stopped in Squeegee (no sheet rows) can still bring the money of those who owe or
// have credit, as Lapsed customers (Astra, steps 5-6 finding 7), and check the accounts already here against it: a
// newer figure, debits Squeegee has resolved (short check finding 5). Unfinished history is resumeHistory's.
export function moneyOnly(m: SqueegeeMoney): boolean {
  return m.accounts.some((a) => !a.conflict && ((a.stopped && !a.already && (a.squeegee_pence !== 0 || a.collections.length > 0)) || a.already !== null))
}

// History still to arrive for accounts already here (an earlier import of this file stopped part way): their lines,
// sent again under the import that brought each account, whether or not this backup's money is accepted (finding 4).
export function resumeHistory(state: MoneyState): { batch_key: string; squeegee_ref: string; lines: MoneyAccount['lines'] }[] {
  // The backup's account for each known one: by Squeegee id first; by reference only when the ids agree or Roundly has
  // none (a reference reused for someone else never takes their history; short check finding 4).
  const theirs = (k: KnownAccount): MoneyAccount | undefined =>
    (k.squeegee_id ? state.m.accounts.find((a) => a.squeegee_id === k.squeegee_id) : undefined)
    ?? state.m.accounts.find((a) => a.squeegee_ref === k.squeegee_ref && (!k.squeegee_id || a.squeegee_id === k.squeegee_id))
  return state.known
    .filter((k) => k.state === 'incomplete' && !!k.batch_key && (theirs(k)?.lines.length ?? 0) > 0)
    .map((k) => ({ batch_key: k.batch_key!, squeegee_ref: k.squeegee_ref, lines: theirs(k)!.lines }))
}

// The account already here that a Squeegee customer is: the exact Squeegee id first; a reference alone only when that
// account has no id or the same one (short check finding 4).
export function knownFor(known: KnownAccount[], ref: string, id: string): KnownAccount | undefined {
  const byRef = known.find((k) => k.squeegee_ref === ref)
  return known.find((k) => !!k.squeegee_id && k.squeegee_id === id) ?? (byRef && (!byRef.squeegee_id || byRef.squeegee_id === id) ? byRef : undefined)
}

// The words for each answer, for the owner.
export function optionWords(q: MoneyQuestion, option: string, houses: string[] = []): string {
  if (option.startsWith('house:')) return houses[Number(option.slice(6))] || `House ${Number(option.slice(6)) + 1}`
  return ({
    lapsed: 'Bring them in as Lapsed, with the money',
    leave_out: q.kind === 'unreadable' ? 'Leave it out' : 'Leave them and the money out',
    paid: 'It was paid',
    add_back: 'Add it back: still owed',
    as_squeegee: 'Count it as Squeegee does',
    cancelled: 'Cancelled: it counts nothing',
    stands: 'It stands: never cancelled',
    accept: 'Accept the new figure',
    keep: 'Keep what Roundly has',
  } as Record<string, string>)[option] ?? option
}
export const KIND_WORDS: Record<MoneyQuestion['kind'], string> = {
  stopped: 'Stopped or archived in Squeegee, with money',
  failed: 'Failed payments Squeegee still counts as paid',
  contradiction: "Records that don't add up",
  unreadable: "Records Roundly can't read",
  two_houses: 'One customer at more than one house',
  newer: 'Already brought over: what changed',
}

// The answers given, with "same answer for all" for one kind applied to every question of it that offers it.
export function answerAll(state: MoneyState, kind: MoneyQuestion['kind'], option: string): Record<string, string> {
  const out = { ...state.answers }
  for (const q of state.m.questions) if (q.kind === kind && q.options.includes(option)) out[q.id] = option
  return out
}

// What a backup's money comes to with the answers so far.
export const settled = (state: MoneyState): Settled => settleMoney(state.m, state.answers)

const norm = (v: string): string => v.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

export interface Person { line: number; ref: string; address: string }
export interface Existing { customer_id: number; ref: string; address: string }
export interface Placed {
  // By the customer's line on the check screen: the account that comes with them.
  rows: Map<number, Brought>
  // Accounts for customers already on the round.
  accounts: { customer_id: number; brought: Brought }[]
  // Stopped in Squeegee, brought in as Lapsed with the money (rows of their own).
  lapsed: { name: string; address: string; phone?: string; email?: string; status: 'lapsed'; brought: Brought }[]
  // Accounts with nowhere to go (their customer unticked, or not coming in): named, not brought.
  notPlaced: { name: string; pence: number }[]
  // A newer backup's figures (Liam Q10), with what Roundly had when the owner checked, and its direct debits.
  corrections: { squeegee_ref: string; squeegee_id: string; baseline_pence: number; target_pence: number; as_of: string; debits: MoneyAccount['debits']; collections: Brought['collections'] }[]
  // The check screen's totals for what is sent, for the server to be checked against after every part.
  expected: { owed_pence: number; owed_customers: number; credit_pence: number; credit_customers: number; accounts: number }
  // The history of every account sent, by Squeegee ref.
  history: { squeegee_ref: string; lines: MoneyAccount['lines'] }[]
}

function broughtOf(a: SettledAccount, asOf: string, lines: number): Brought {
  return {
    squeegee_ref: a.squeegee_ref,
    squeegee_id: a.squeegee_id,
    as_of: new Date(asOf).toISOString().slice(0, 19) + 'Z',
    squeegee_pence: a.squeegee_pence,
    opening_pence: a.opening_pence,
    collections: a.collections.map((c) => ({ ext_id: c.ext_id, provider: c.provider, provider_payment_id: c.provider_payment_id, amount_pence: c.amount_pence, on_date: /^\d{4}-\d{2}-\d{2}$/.test(c.on_date) ? c.on_date : null, squeegee_status: c.squeegee_status, opening_effect_pence: c.opening_effect_pence })),
    history_expected: lines,
  }
}

// Each settled account onto the customer that holds it: the house the owner chose (Liam Q7: the first by default),
// matched by Squeegee ref and address among the customers being added, else among those already here. Stopped ones
// answered "Lapsed" come in as their own customer. An account whose customer is not coming in is named, not brought.
export function placeMoney(state: MoneyState, s: Settled, people: Person[], existing: Existing[]): Placed {
  const asOf = state.m.as_of!
  const byRef = new Map(state.m.accounts.map((a) => [a.squeegee_ref, a]))
  const knownOf = (ref: string, id: string): KnownAccount | undefined => knownFor(state.known, ref, id)
  const out: Placed = {
    rows: new Map(), accounts: [], lapsed: [], notPlaced: [],
    // Named by the reference Roundly holds for the account (the reader found it by Squeegee id first), with the
    // Squeegee id for the server to check, and the debits of the backup's own account (by that id).
    corrections: s.corrections.map((c) => ({ squeegee_ref: c.squeegee_ref, squeegee_id: c.squeegee_id, baseline_pence: c.baseline_pence, target_pence: c.target_pence, as_of: new Date(asOf).toISOString().slice(0, 19) + 'Z', debits: c.debits,
      collections: c.collections.map((k) => ({ ext_id: k.ext_id, provider: k.provider, provider_payment_id: k.provider_payment_id, amount_pence: k.amount_pence, on_date: /^\d{4}-\d{2}-\d{2}$/.test(k.on_date) ? k.on_date : null, squeegee_status: k.squeegee_status, opening_effect_pence: k.opening_effect_pence })) })),
    expected: { owed_pence: 0, owed_customers: 0, credit_pence: 0, credit_customers: 0, accounts: 0 }, history: [],
  }
  // A check with nothing moving (debits only) from a backup no newer than the one Roundly has: nothing to say.
  out.corrections = out.corrections.filter((c) => c.baseline_pence !== c.target_pence || (state.known.find((k) => k.squeegee_ref === c.squeegee_ref)?.as_of ?? '') < (state.m.as_of ?? ''))
  // A reference Roundly holds for a different Squeegee customer: named, nothing brought.
  for (const c of s.conflicts) out.notPlaced.push({ name: `${c.name} (their Squeegee reference ${c.squeegee_ref} belongs to someone else here)`, pence: c.squeegee_pence })
  const taken = new Set<number>()
  for (const a of s.accounts) {
    if (knownOf(a.squeegee_ref, a.squeegee_id)) continue
    const acc = byRef.get(a.squeegee_ref)
    if (!acc) continue
    const house = acc.houses[a.house] ?? acc.houses[0] ?? ''
    const fits = (p: { ref: string; address: string }): boolean => p.ref === a.squeegee_ref && (acc.houses.length <= 1 || norm(p.address) === norm(house))
    const brought = broughtOf(a, asOf, acc.lines.length)
    const person = people.find((p) => !taken.has(p.line) && fits(p))
    const here = person ? undefined : existing.find(fits)
    if (person) {
      taken.add(person.line)
      out.rows.set(person.line, brought)
    } else if (here) {
      out.accounts.push({ customer_id: here.customer_id, brought })
    } else if (a.lapsed) {
      out.lapsed.push({ name: acc.name, address: house, ...(acc.phone ? { phone: acc.phone } : {}), ...(acc.email ? { email: acc.email } : {}), status: 'lapsed', brought })
    } else {
      out.notPlaced.push({ name: acc.name, pence: a.opening_pence })
      continue
    }
    out.expected.accounts++
    if (a.opening_pence > 0) { out.expected.owed_pence += a.opening_pence; out.expected.owed_customers++ }
    if (a.opening_pence < 0) { out.expected.credit_pence -= a.opening_pence; out.expected.credit_customers++ }
    if (acc.lines.length) out.history.push({ squeegee_ref: a.squeegee_ref, lines: acc.lines })
  }
  return out
}

// History in chunks of at most 500 lines AND at most 200 KB of JSON (the server takes 256 KB a request; Astra, steps
// 5-6 finding 5), an account split across chunks when it must be.
export const CHUNK_BYTES = 200_000
export function historyChunks(history: Placed['history'], size = 500, maxBytes = CHUNK_BYTES): { squeegee_ref: string; lines: MoneyAccount['lines'] }[][] {
  const encoder = new TextEncoder()
  const chunks: { squeegee_ref: string; lines: MoneyAccount['lines'] }[][] = []
  let chunk: { squeegee_ref: string; lines: MoneyAccount['lines'] }[] = []
  let n = 0
  let bytes = 0
  for (const h of history) {
    let part: { squeegee_ref: string; lines: MoneyAccount['lines'] } | null = null
    for (const line of h.lines) {
      const size1 = encoder.encode(JSON.stringify(line)).length + 1
      const overhead = part ? 0 : encoder.encode(JSON.stringify({ squeegee_ref: h.squeegee_ref, lines: [] })).length + 1
      if (n > 0 && (n + 1 > size || bytes + size1 + overhead > maxBytes)) {
        chunks.push(chunk)
        chunk = []
        n = 0
        bytes = 0
        part = null
      }
      if (!part) {
        part = { squeegee_ref: h.squeegee_ref, lines: [] }
        chunk.push(part)
        bytes += encoder.encode(JSON.stringify({ squeegee_ref: h.squeegee_ref, lines: [] })).length + 1
      }
      part.lines.push(line)
      n++
      bytes += size1
    }
  }
  if (chunk.length) chunks.push(chunk)
  return chunks
}

// The card's top lines (PLAN section 4): owed and credit apart, never netted.
export function totalsWords(state: MoneyState, s: Settled, timeZone: string): string[] {
  const sq = state.m.squeegee
  const n = (k: number, one: string, many: string): string => `${k} ${k === 1 ? one : many}`
  const lines = [`Squeegee says ${pounds(sq.owed_pence)} owed by ${n(sq.owed_customers, 'customer', 'customers')}. Roundly will show ${pounds(s.totals.owed_pence)}.`]
  if (s.totals.credit_pence) lines.push(`${pounds(s.totals.credit_pence)} credit for ${n(s.totals.credit_customers, 'customer', 'customers')}, set aside for their next cleans.`)
  if (s.totals.collecting_pence) lines.push(`${pounds(s.totals.collecting_pence)} still collecting by direct debit, ${n(s.totals.collecting_customers, 'customer', 'customers')}.`)
  if (state.m.as_of) lines.push(`As of ${asOfWords(state.m.as_of, timeZone)}.`)
  return lines
}

// Items cut into parts of at most `count` and at most `maxBytes` of JSON (the server takes 256 KB a request; Astra,
// last check finding 2): accounts and corrections can carry many direct debits each.
export function partsOf<T>(items: T[], count = 200, maxBytes = CHUNK_BYTES): T[][] {
  const encoder = new TextEncoder()
  const parts: T[][] = []
  let part: T[] = []
  let bytes = 0
  for (const item of items) {
    const size = encoder.encode(JSON.stringify(item)).length + 1
    if (part.length && (part.length >= count || bytes + size > maxBytes)) {
      parts.push(part)
      part = []
      bytes = 0
    }
    part.push(item)
    bytes += size
  }
  if (part.length) parts.push(part)
  return parts
}
